import { Option } from "@inrev/core";
import { useEffect } from "react";
import type { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import {
	CurrencyPercentInput,
	type CurrencyPercentInputMode,
} from "../inputs/CurrencyPercentInput";

export type FormCurrencyPercentProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = {
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	formMethods: UseFormReturn<TFieldValues, any, TTransformedValues>;
	referenceSourceName: Path<TFieldValues>; // The field that acts as the base amount
	currencyName: Path<TFieldValues>;
	percentName: Path<TFieldValues>;
	amountTypeName: Path<TFieldValues>;
	defaultAmountType?: CurrencyPercentInputMode;
	compact?: boolean;
	readonly?: boolean;
	updateOnBlur?: boolean;
	errorMessage?: string;
	className?: string;
	testId?: string;
};

const toValue = <TFieldValues extends FieldValues>(value: unknown) =>
	value as PathValue<TFieldValues, Path<TFieldValues>>;

export const FormCurrencyPercent = <
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
>({
	formMethods,
	referenceSourceName,
	currencyName,
	percentName,
	amountTypeName,
	defaultAmountType,
	compact,
	readonly,
	updateOnBlur,
	errorMessage,
	className,
	testId,
}: FormCurrencyPercentProps<TFieldValues, TTransformedValues>) => {
	const { setValue, watch, trigger, formState, getFieldState } = formMethods;

	const referenceValue = watch(referenceSourceName);
	const percentValue = watch(percentName);
	const currencyValue = watch(currencyName);
	const amountType = watch(amountTypeName);

	const baseAmount = parseFloat(referenceValue?.toString() || "0");
	const isDisabled = !referenceValue || baseAmount <= 0;

	const calculatePercent = (currencyVal: Option<number>) => {
		if (Option.isNone(currencyVal)) return undefined;
		if (!baseAmount) return 0;
		return Math.round((currencyVal / baseAmount) * 1000) / 1000;
	};

	const calculateCurrency = (percentVal: Option<number>) => {
		if (Option.isNone(percentVal)) return undefined;
		if (!baseAmount) return 0;
		return percentVal * baseAmount;
	};

	const areValuesBalanced = (currencyVal: number, percentVal: number, bidAmount: number) => {
		if (bidAmount <= 0 || currencyVal <= 0 || percentVal <= 0) return false;
		const expectedCurrency = percentVal * bidAmount;
		const expectedPercent = currencyVal / bidAmount;
		const currencyDifference = Math.abs(currencyVal - expectedCurrency);
		const percentDifference = Math.abs(percentVal - expectedPercent);
		return currencyDifference < 0.01 && percentDifference < 0.01;
	};

	// Auto-recalculate based on amountType on first render and when amountType changes
	useEffect(() => {
		if (baseAmount <= 0) return;

		const currencyVal = parseFloat(currencyValue?.toString() || "0");
		const percentVal = parseFloat(percentValue?.toString() || "0");
		if (areValuesBalanced(currencyVal, percentVal, baseAmount)) return;

		if (amountType === "percent") {
			if (percentVal > 0) {
				setValue(currencyName, toValue<TFieldValues>(calculateCurrency(percentVal)));
			}
		} else if (currencyVal > 0) {
			setValue(percentName, toValue<TFieldValues>(calculatePercent(currencyVal)));
		}
	}, [amountType, referenceValue, percentValue, currencyValue, setValue]);

	// When the reference (base) amount changes, recompute currency from the existing percent
	useEffect(() => {
		const currentPercent = parseFloat(percentValue?.toString() || "0");
		if (currentPercent > 0) {
			setValue(currencyName, toValue<TFieldValues>(calculateCurrency(currentPercent)));
		}
	}, [referenceValue, setValue, currencyName, percentName]);

	// Seed the amount type with the provided default when unset
	useEffect(() => {
		if ((!amountType || amountType === "") && defaultAmountType) {
			setValue(amountTypeName, toValue<TFieldValues>(defaultAmountType));
		}
	}, [amountType, defaultAmountType]);

	const onBlur = () => {
		if (formState.isSubmitted) trigger([currencyName, percentName]);
	};

	const onCurrencyChange = (value: string | number) => {
		const numeric = typeof value === "number" ? value : parseFloat(value);
		setValue(currencyName, toValue<TFieldValues>(value), { shouldDirty: true });
		setValue(percentName, toValue<TFieldValues>(calculatePercent(numeric)));
	};

	const onPercentChange = (value: number | undefined) => {
		setValue(percentName, toValue<TFieldValues>(value), { shouldDirty: true });
		setValue(currencyName, toValue<TFieldValues>(calculateCurrency(value)));
	};

	const onAmountTypeChange = (value: CurrencyPercentInputMode) => {
		setValue(amountTypeName, toValue<TFieldValues>(value));
	};

	const error =
		getFieldState(currencyName, formState).error ?? getFieldState(percentName, formState).error;

	return (
		<div className="relative">
			<CurrencyPercentInput
				inputMode={amountType}
				setInputMode={onAmountTypeChange}
				currencyValue={currencyValue}
				percentValue={percentValue}
				onCurrencyChange={onCurrencyChange}
				onPercentChange={onPercentChange}
				baseAmount={baseAmount}
				onBlur={onBlur}
				updateOnBlur={updateOnBlur}
				compact={compact}
				error={!!error}
				disabled={isDisabled}
				readonly={readonly || isDisabled}
				className={className}
				testId={testId}
			/>
			{error && (
				<FormFieldError
					error={error}
					errorMessage={errorMessage}
					className={compact ? "left-[26px]" : "left-[41px]"}
				/>
			)}
		</div>
	);
};
